import { useMemo, useState } from 'react';
import type { Card, Etapa, CampoConfig } from '../../types';
import { useToast } from '../../contexts/ToastContext';
import { apiUrl } from '../../utils/apiBase';

interface ModalFatiarCardProps {
  card: Card;
  flowId: string;
  /** Etapas do fluxo, só para resolver o nome da etapa de destino. */
  etapas?: Etapa[];
  etapaAtual: Etapa;
  userEmail?: string;
  empresaId?: string;
  onClose: () => void;
  /** Disparado após a fatia nascer. `camposRestantes` é o que sobrou no card de origem. */
  onFatiado: (novoCard: any, camposRestantes: Record<string, any>) => void;
}

/** Resume o valor de um campo em uma linha, só para o usuário conferir o que vai levar. */
const resumirValor = (valor: any): string => {
  if (valor === null || valor === undefined || valor === '') return '—';
  if (Array.isArray(valor)) {
    if (valor.length === 0) return '—';
    const primeiro = valor[0];
    if (primeiro && typeof primeiro === 'object') {
      const nomes = valor.map((f: any) => f?.name || f?.fileName || 'arquivo');
      return `${valor.length} arquivo(s): ${nomes.join(', ')}`;
    }
    return valor.join(', ');
  }
  if (typeof valor === 'object') {
    const entradas = Object.entries(valor).filter(([, v]) => v !== '' && v !== null && v !== undefined);
    if (entradas.length === 0) return '—';
    return entradas.map(([k, v]) => `${k}: ${v}`).join(' · ');
  }
  return String(valor);
};

const temValor = (valor: any): boolean => {
  if (valor === null || valor === undefined || valor === '') return false;
  if (Array.isArray(valor)) return valor.length > 0;
  if (typeof valor === 'object') {
    return Object.values(valor).some(v => v !== '' && v !== null && v !== undefined);
  }
  return true;
};

const ModalFatiarCard = ({ card, flowId, etapas, etapaAtual, userEmail, empresaId, onClose, onFatiado }: ModalFatiarCardProps) => {
  const { success, error: toastError } = useToast();
  const cfg = etapaAtual.configuracaoFatiamento;

  const [selecionados, setSelecionados] = useState<string[]>([]);
  const [titulo, setTitulo] = useState('');
  const [fatiando, setFatiando] = useState(false);

  const camposSempreJuntos = cfg?.camposSempreJuntos || [];
  const origemPerdeCampos = !(cfg?.origemMantemCampos ?? false);

  // Campos que o usuário pode marcar: liberados na etapa (ou todos), menos os fixos,
  // e apenas os que realmente têm valor — não faz sentido fatiar campo vazio.
  const camposDisponiveis: CampoConfig[] = useMemo(() => {
    const liberados = cfg?.camposFatiaveis || [];
    return (etapaAtual.campos || []).filter(campo => {
      if (camposSempreJuntos.includes(campo.id)) return false;
      if (liberados.length > 0 && !liberados.includes(campo.id)) return false;
      return temValor(card.campos?.[campo.id]);
    });
  }, [etapaAtual.campos, cfg, card.campos, camposSempreJuntos]);

  const camposFixosComValor: CampoConfig[] = useMemo(() => {
    return (etapaAtual.campos || []).filter(
      campo => camposSempreJuntos.includes(campo.id) && temValor(card.campos?.[campo.id])
    );
  }, [etapaAtual.campos, camposSempreJuntos, card.campos]);

  const etapaDestinoNome = useMemo(() => {
    const destinoId = cfg?.etapaDestinoId || card.etapaId;
    return etapas?.find(e => String(e.id) === String(destinoId))?.nome || etapaAtual.nome;
  }, [cfg, card.etapaId, etapas, etapaAtual.nome]);

  const numeroFatia = (card.fatiasIds?.length || 0) + 1;

  const tituloPrevisto = useMemo(() => {
    if (titulo.trim()) return titulo.trim();
    const modelo = cfg?.prefixoTitulo || '[FATIA {n}] {titulo}';
    return modelo.replace('{n}', String(numeroFatia)).replace('{titulo}', card.titulo || '');
  }, [titulo, cfg, numeroFatia, card.titulo]);

  const toggle = (campoId: string) => {
    setSelecionados(atual =>
      atual.includes(campoId) ? atual.filter(id => id !== campoId) : [...atual, campoId]
    );
  };

  const getAuthParams = () => {
    const params = new URLSearchParams();
    if (userEmail) params.set('userEmail', userEmail);
    if (empresaId) params.set('empresaId', empresaId);
    return params.toString();
  };

  const handleFatiar = async () => {
    if (selecionados.length === 0 && camposFixosComValor.length === 0) {
      toastError('Marque ao menos um campo para levar na fatia');
      return;
    }

    try {
      setFatiando(true);
      const res = await fetch(`${apiUrl('/cards/fatiar')}?${getAuthParams()}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          cardId: card.id,
          flowId,
          camposSelecionados: selecionados,
          titulo: titulo.trim() || undefined
        })
      });

      const data = await res.json();
      if (data.success) {
        success(`Fatia criada: ${data.card?.titulo || tituloPrevisto}`);
        onFatiado(data.card, data.camposRestantes || {});
        onClose();
      } else {
        toastError(data.error || 'Erro ao fatiar o card');
      }
    } catch {
      toastError('Erro ao fatiar o card');
    } finally {
      setFatiando(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[85] p-4" onClick={onClose}>
      <div
        className="bg-white rounded-2xl shadow-2xl max-w-xl w-full max-h-[90vh] flex flex-col overflow-hidden"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="bg-gray-50 border-b border-gray-200 px-6 py-4 flex items-center gap-3">
          <div className="p-2 bg-orange-50 rounded-lg text-lg leading-none">🔪</div>
          <div className="min-w-0">
            <h2 className="text-lg font-bold text-gray-800">{cfg?.rotulo || 'Fatiar card'}</h2>
            <p className="text-sm text-gray-500 truncate">
              Escolha o que vai para a nova fatia de “{card.titulo}”
            </p>
          </div>
        </div>

        {/* Conteúdo */}
        <div className="p-6 space-y-4 overflow-y-auto">
          {camposDisponiveis.length === 0 && camposFixosComValor.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-gray-500 text-sm mb-1">Nenhum campo preenchido disponível para fatiar.</p>
              <p className="text-gray-400 text-xs">
                Preencha os campos do card, ou revise quais campos a etapa “{etapaAtual.nome}” libera para fatiamento.
              </p>
            </div>
          ) : (
            <>
              {/* Campos escolhíveis */}
              {camposDisponiveis.length > 0 && (
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-sm font-medium text-gray-700">Campos que vão na fatia</label>
                    <button
                      type="button"
                      onClick={() =>
                        setSelecionados(
                          selecionados.length === camposDisponiveis.length ? [] : camposDisponiveis.map(c => c.id)
                        )
                      }
                      className="text-xs text-orange-600 hover:text-orange-700 font-medium"
                    >
                      {selecionados.length === camposDisponiveis.length ? 'Limpar seleção' : 'Marcar todos'}
                    </button>
                  </div>
                  <div className="space-y-1.5">
                    {camposDisponiveis.map(campo => {
                      const marcado = selecionados.includes(campo.id);
                      return (
                        <label
                          key={campo.id}
                          className={`flex items-start gap-2.5 p-2.5 rounded-lg border cursor-pointer transition-all ${
                            marcado ? 'border-orange-300 bg-orange-50' : 'border-gray-200 bg-white hover:bg-gray-50'
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={marcado}
                            onChange={() => toggle(campo.id)}
                            className="h-4 w-4 mt-0.5 rounded border-gray-300 text-orange-600 focus:ring-orange-500"
                          />
                          <span className="min-w-0 flex-1">
                            <span className="block text-sm font-medium text-gray-800">{campo.nome}</span>
                            <span className="block text-xs text-gray-500 truncate" title={resumirValor(card.campos?.[campo.id])}>
                              {resumirValor(card.campos?.[campo.id])}
                            </span>
                          </span>
                        </label>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Campos que acompanham sempre */}
              {camposFixosComValor.length > 0 && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <p className="text-xs font-semibold text-blue-800 mb-1.5">
                    Vão junto automaticamente (e continuam no card de origem)
                  </p>
                  <div className="flex flex-wrap gap-1.5">
                    {camposFixosComValor.map(campo => (
                      <span key={campo.id} className="text-[11px] px-2 py-0.5 bg-white text-blue-700 border border-blue-200 rounded-full">
                        {campo.nome}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Título da fatia */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Título da fatia (opcional)</label>
                <input
                  type="text"
                  value={titulo}
                  onChange={e => setTitulo(e.target.value)}
                  placeholder={tituloPrevisto}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                />
                <p className="text-xs text-gray-400 mt-1">Deixe vazio para usar: {tituloPrevisto}</p>
              </div>

              {/* O que vai acontecer */}
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 space-y-1.5">
                <p className="text-[11px] text-gray-600">
                  Nasce um card novo, com código próprio, na etapa <strong>{etapaDestinoNome}</strong>.
                </p>
                {origemPerdeCampos && selecionados.length > 0 && (
                  <p className="text-[11px] text-amber-800">
                    <strong>{selecionados.length} campo(s)</strong> saem de “{card.titulo}” e passam a viver na fatia. O
                    conteúdo continua guardado no histórico de etapas do card de origem.
                  </p>
                )}
                {!origemPerdeCampos && (
                  <p className="text-[11px] text-gray-600">O card de origem continua com todos os campos.</p>
                )}
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3 flex-none">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors text-sm"
          >
            Cancelar
          </button>
          <button
            onClick={handleFatiar}
            disabled={fatiando || (selecionados.length === 0 && camposFixosComValor.length === 0)}
            className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {fatiando ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                Fatiando...
              </>
            ) : (
              <>🔪 {cfg?.rotulo || 'Fatiar card'}</>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ModalFatiarCard;
